import React, { useMemo } from 'react';

import { useTranslationX } from 'i18n';
import { useAuthState } from 'store/auth';
import { TableData } from 'components/containers';
import { FieldOption, FormContextValue } from 'components/form/types';
import { TotalLabel } from 'pages/proposal';
import { Profile } from 'pages/register/profile';

import styles from './Financial.module.scss';

// Vinculo de instituição = Outros.
const VALUE_DEFAULT_VINCULO_INSTITUICAO = 2;

type Props = {
  parteId: number;
  fonteRecursoStatus: number;
};

const HumanResources = (props: Props) => {
  const { parteId, fonteRecursoStatus } = props;
  const { tx } = useTranslationX('hr', 'update_wp');
  const { profile } = useAuthState();

  const isSupervisor = profile === Profile.SUPERVISOR;

  const groupBy = useMemo(
    (): (string | number)[][] => [
      ['fonteRecurso', Number(parteId)],
      ['fonteRecursoStatus', Number(fonteRecursoStatus)],
    ],
    [parteId, fonteRecursoStatus]
  );

  const onObservedFieldChange = (
    id: string,
    value: any,
    form: FormContextValue,
    params: string[] | undefined,
    ...stateFunctions: React.Dispatch<React.SetStateAction<any>>[]
  ) => {
    const strings = id.split('.');

    if (strings.length > 0) {
      const vinculoTrabalhoFieldName =
        strings.slice(0, strings.length - 1).join('.') + '.vinculoTrabalho';
      const metadata = form.getFieldMetadata(vinculoTrabalhoFieldName);
      const options = metadata.options as FieldOption[];

      if (
        options?.find(
          (o: FieldOption) =>
            o.value === value &&
            (o.value === 2 || o.value === 3 || o.value === 4) /* CLTs e Estagio */
        )
      ) {
        if (
          strings &&
          strings.length > 0 &&
          strings[strings.length - 1] === 'categoriaBolsa'
        ) {
          stateFunctions[0](true);
          form.setFieldValue(id, undefined, true);
        }

        if (
          strings &&
          strings.length > 0 &&
          strings[strings.length - 1] === 'vinculoInstituicao'
        ) {
          stateFunctions[0](true);
          form.setFieldValue(id, VALUE_DEFAULT_VINCULO_INSTITUICAO, true);
        }
      } else {
        stateFunctions[0](false);
      }
    }
  };

  const onEncargosFieldChange = (
    id: string,
    value: any,
    form: FormContextValue,
    params: string[] | undefined
  ) => {
    const strings = id.split('.');
    const fieldData = form.getFieldData(id);
    const isHumanUpdated =
      fieldData?.controlState?.modified || fieldData?.controlState?.changed;

    if (strings.length > 0 && isHumanUpdated) {
      const basePath = strings.slice(0, strings.length - 1).join('.');
      const remuneracaoFieldName = `${basePath}.remuneracao`;
      const vinculoTrabalhoFieldName = `${basePath}.vinculoTrabalho`;
      const encargosFieldName = `${basePath}.encargos`;

      const remuneracao = form.getFieldValue(remuneracaoFieldName);
      const vinculoTrabalhoValue = form.getFieldValue(vinculoTrabalhoFieldName);

      if (remuneracao !== undefined && vinculoTrabalhoValue !== undefined) {
        const vinculoMetadata = form.getFieldMetadata(vinculoTrabalhoFieldName);
        const vinculoOptions = vinculoMetadata.options as FieldOption[];

        if (Array.isArray(vinculoOptions)) {
          const vinculoOption = vinculoOptions.find(
            (o: FieldOption) => o.value === vinculoTrabalhoValue
          );

          if (vinculoOption && vinculoOption.params) {
            const paramsArray = vinculoOption.params.split(',');
            const percentual = Number(paramsArray[0]) || 0;
            const valorFixo = Number(paramsArray[1]) || 0;
            const encargosCalculado = Number(
              ((remuneracao * percentual) / 100 + valorFixo).toFixed(2)
            );

            form.setFieldValue(encargosFieldName, encargosCalculado, true);
          }
        }
      }
    }
  };

  return (
    <div
      className={styles.financial}
      style={{
        minWidth: '88vw',
      }}
    >
      <TotalLabel tableName="rh" columnName="total" groupBy={groupBy} />

      <div className={styles.table}>
        <TableData
          name="rh"
          groupBy={groupBy}
          hiddenFields={[
            'mesesExecutados',
            'duracaoBruta',
            'mesInicio',
            'mesExecucao',
            'fonteRecursoStatus',
          ]}
          textSize="small"
          config={[
            {
              title: tx('institution'),
              field: 'vinculoInstituicao',
              align: 'left',
              type: 'select',
              weight: 0.8,
              fieldProps: {
                onObservedFieldChange,
                observeFields: ['vinculoTrabalho'],
              },
            },
            { title: tx('position'), field: 'cargo', weight: 1.2 },
            {
              title: tx('relationship'),
              field: 'vinculoTrabalho',
              type: 'select',
              weight: 0.8,
              fieldProps: {
                onObservedFieldChange: onEncargosFieldChange,
                observeFields: ['remuneracao', 'vinculoTrabalho'],
              },
            },
            {
              title: tx('category'),
              field: 'categoriaBolsa',
              type: 'select',
              weight: 1.2,
              fieldProps: {
                onObservedFieldChange,
                observeFields: ['vinculoTrabalho'],
              },
            },
            {
              title: tx('salary'),
              field: 'remuneracao',
              fieldProps: {
                align: 'right',
                onObservedFieldChange: onEncargosFieldChange,
                observeFields: ['vinculoTrabalho'],
              },
              weight: 0.7,
              align: 'right',
            },
            {
              title: tx('taxes'),
              field: 'encargos',
              align: 'center',
              mutabilityPolicy: isSupervisor ? 'hardForce' : undefined,
              fieldProps: {
                align: 'right',
                type: isSupervisor ? undefined : 'readonly',
              },
              weight: 0.7,
            },
            {
              title: tx('duration'),
              field: 'duracao',
              type: 'computed',
              align: 'center',
              fieldProps: {
                align: 'center',
                expression: 'mesesDisponiveis + mesesSolicitados + mesesExecutados',
              },
              metadata: {
                rules: {
                  min: 1,
                  required: true,
                  customMessage: tx('emptyDuration'),
                },
              },
              weight: 0.5,
            },
            {
              title: tx('available-duration'),
              field: 'mesesDisponiveis',
              mutabilityPolicy: 'force',
              fieldProps: { align: 'center' },
              metadata: {
                rules: {
                  min: 0,
                },
              },
              align: 'right',
              weight: 0.5,
            },
            {
              title: tx('total'),
              field: 'total',
              type: 'computed',
              align: 'right',
              metadata: { rules: { mask: { pattern: 'currency' } } },
              fieldProps: {
                expression: '(remuneracao + encargos) * duracao',
                align: 'right',
                omit: true,
              },
              weight: 0.7,
            },
          ]}
          style={{
            table: {
              maxWidth: '95%',
            },
          }}
        />
      </div>
    </div>
  );
};

export default HumanResources;
